import { AddressCreateWithoutCustomerInput, CheckoutOrderQuery } from '@/__generated__/graphql';
import { useMutation } from '@apollo/client';
import { Alert, Button, Card, Group, Select, Stack, Text } from '@mantine/core';
import { openModal } from '@mantine/modals';
import { IconEdit, IconPlus } from '@tabler/icons-react';
import { setAddressOnCheckoutOrderDocument } from '../graphql/mutations';
import { checkoutOrderDocument } from '../graphql/queries';
import { AddressForm } from './OrderCheckoutAddressForm';
import { useCallback, useEffect } from 'react';

export const AddressSelect = ({
  order,
  isPersonal,
}: {
  order: CheckoutOrderQuery['checkoutOrder'];
  isPersonal: boolean;
}) => {
  const [setAddressOnActiveOrder] = useMutation(setAddressOnCheckoutOrderDocument, {
    refetchQueries: [checkoutOrderDocument],
  });

  const handleUpsertUserAddress = useCallback(
    (address?: AddressCreateWithoutCustomerInput) =>
      openModal({
        modalId: 'create-user-address',
        title: 'Vytvoriť adresu',
        children: <AddressForm orderId={order.id} address={address} isPersonal={isPersonal} />,
        closeOnClickOutside: false,
      }),
    [isPersonal, order.id]
  );

  useEffect(() => {
    if (
      !order.address ||
      (order.address &&
        (order.address.name.length === 0 ||
          order.address.streetLine.length === 0 ||
          order.address.postCode.length === 0 ||
          order.address.city.length === 0))
    )
      handleUpsertUserAddress(order.address ?? undefined);
  }, [handleUpsertUserAddress, isPersonal, order.address]);

  return (
    <Card p="md" withBorder component={Stack}>
      <Group justify="space-between">
        <Text fw={500}>Adresa doručenia</Text>
        {!isPersonal && (order.customer.addresses?.length ?? 0) > 0 && (
          <Button
            size="xs"
            leftSection={<IconPlus size={16} />}
            onClick={() => handleUpsertUserAddress()}
          >
            Nová adresa
          </Button>
        )}
      </Group>
      {order.customer.addresses?.length === 0 ? (
        <AddressForm orderId={order.id} isPersonal={isPersonal} />
      ) : (
        <Stack>
          <Select
            data={(order.customer.addresses ?? []).map((address) => ({
              value: address.id,
              label: `${address.name}, ${address.streetLine}, ${address.city}, ${address.country}, ${address.phone}`,
            }))}
            value={order.address?.addressId}
            onChange={(addressId) =>
              addressId &&
              void setAddressOnActiveOrder({ variables: { orderId: order.id, addressId } })
            }
          />
          {order.address && (
            <>
              {!isPersonal &&
                (order.address.name.length === 0 ||
                  order.address.streetLine.length === 0 ||
                  order.address.postCode.length === 0 ||
                  order.address.city.length === 0) && (
                  <Alert color="red" title="Chýbajúce údaje v adrese">
                    Zrejme ste použili adresu z osobného odberu, ktorá nemá všetky údaje vyplnené.
                    Prosím doplňte chýbajúce údaje stlačením tlačidla &apos;Upraviť adresu&apos;.
                  </Alert>
                )}
              <Group justify="space-between">
                {isPersonal ? (
                  <Text size="sm">Telefónne číslo: {order.address.phone}</Text>
                ) : (
                  <Stack gap={0}>
                    <Text size="sm">{order.address.name}</Text>
                    <Text size="sm">{order.address.streetLine}</Text>
                    {order.address.streetDescription && (
                      <Text size="sm">{order.address.streetDescription}</Text>
                    )}
                    <Text size="sm">
                      {order.address.postCode}, {order.address.city}
                    </Text>
                    <Text size="sm">
                      {order.address.country}, {order.address.phone}
                    </Text>
                  </Stack>
                )}
                <Button
                  size="xs"
                  leftSection={<IconEdit size={16} />}
                  onClick={() =>
                    handleUpsertUserAddress(
                      order.customer.addresses?.find(
                        (address) => address.id === order.address?.addressId
                      )
                    )
                  }
                >
                  Upraviť {isPersonal ? 'telefónne číslo' : 'adresu'}
                </Button>
              </Group>
            </>
          )}
        </Stack>
      )}
    </Card>
  );
};
